/**
 * Likely duplicate teams on one game night.
 *
 * Duplicates happen when a phone checks in a team that the host already
 * typed from a paper sheet, or when two phones of one team both check in.
 * Two teams are likely the same when they link to the same canonical team,
 * or when their names are equal after the same normalization the scoring
 * search uses (teamMatch.norm).
 */
import { norm } from '../components/Scoring/teamMatch';

export interface DupCandidate {
  id: string;
  name: string;
  canonicalTeamId?: number | null;
}

/** Map of team id to the ids of the other teams that look like the same team. */
export function findDuplicates<T extends DupCandidate>(teams: readonly T[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const add = (a: string, b: string) => {
    const list = out.get(a) ?? [];
    if (!list.includes(b)) list.push(b);
    out.set(a, list);
  };
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) {
      const a = teams[i]!;
      const b = teams[j]!;
      const sameCanonical = a.canonicalTeamId != null && a.canonicalTeamId === b.canonicalTeamId;
      const na = norm(a.name);
      const sameName = na !== '' && na === norm(b.name);
      if (sameCanonical || sameName) {
        add(a.id, b.id);
        add(b.id, a.id);
      }
    }
  }
  return out;
}
